<template>
    <div class="vehicle-search-autocomplete" :class="{ 'is-open': isOpen }">
        <div class="vehicle-search-autocomplete__input-wrap">
            <input
                ref="input"
                type="text"
                :value="query"
                :placeholder="placeholder"
                class="form-control vehicle-search-autocomplete__input"
                autocomplete="off"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="isOpen ? 'true' : 'false'"
                :aria-controls="listboxId"
                :aria-activedescendant="activeId"
                @input="onInput($event.target.value)"
                @keydown.down.prevent="moveActive(1)"
                @keydown.up.prevent="moveActive(-1)"
                @keydown.enter.prevent="onEnter"
                @keydown.esc="close"
                @focus="onFocus"
                @blur="onBlur"
            >
            <span class="vehicle-search-autocomplete__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
            </span>
        </div>
        <ul
            v-show="isOpen"
            :id="listboxId"
            class="vehicle-search-autocomplete__list"
            role="listbox"
        >
            <li v-if="loading" class="vehicle-search-autocomplete__state">
                {{ translations.loading }}
            </li>
            <li v-else-if="error" class="vehicle-search-autocomplete__state is-error">
                {{ translations.error }}
            </li>
            <li
                v-else-if="!results.length && query.length >= minChars"
                class="vehicle-search-autocomplete__state"
            >
                {{ translations.no_results }}
            </li>
            <li
                v-for="(row, i) in results"
                :id="listboxId + '-' + i"
                :key="row.type + '/' + row.make_slug + '/' + row.model_slug"
                role="option"
                :aria-selected="i === activeIndex ? 'true' : 'false'"
                :class="['vehicle-search-autocomplete__item', { 'is-active': i === activeIndex }, { 'vehicle-search-autocomplete__item--synthetic': row.type === 'make_all' || row.type === 'attrs' }]"
                @mousedown.prevent="select(row)"
                @mouseenter="activeIndex = i"
            >
                <template v-if="row.type === 'make_all' || row.type === 'attrs'">
                    <i class="fas fa-search mr-1"></i><em>{{ row.label }}</em>
                </template>
                <template v-else>
                    <strong>{{ row.make }}</strong> {{ row.model }}
                </template>
            </li>
        </ul>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
    name: 'VehicleSearchAutocomplete',

    props: {
        commercial:  { type: Boolean, default: false },
        searchUrl:   { type: String,  required: true },
        placeholder: { type: String,  default: 'Keresés márka, modell, karosszéria vagy üzemanyag alapján…' },
        minChars:    { type: Number,  default: 2 },
        i18n:        { type: Object,  default: () => ({}) },
    },

    data() {
        return {
            query:       '',
            results:     [],
            activeIndex: -1,
            isOpen:      false,
            loading:     false,
            error:       false,
            listboxId:   'vsa-' + Math.random().toString(36).slice(2, 8),
            cancelSource: null,
        };
    },

    computed: {
        activeId() {
            return this.activeIndex >= 0 ? this.listboxId + '-' + this.activeIndex : null;
        },
        translations() {
            return Object.assign({
                loading:    'Keresés…',
                no_results: 'Nincs találat',
                error:      'Hiba történt',
            }, this.i18n);
        },
    },

    created() {
        this.debouncedFetch = debounce(this.fetch, 250);
    },

    beforeDestroy() {
        this.cancelPending();
        this.debouncedFetch.cancel();
    },

    methods: {
        onInput(value) {
            this.query       = value;
            this.activeIndex = -1;

            if (value.trim().length < this.minChars) {
                this.results = [];
                this.isOpen  = false;
                this.debouncedFetch.cancel();
                return;
            }
            this.debouncedFetch();
        },

        async fetch() {
            this.cancelPending();
            this.cancelSource = axios.CancelToken.source();
            this.loading = true;
            this.error   = false;

            try {
                const { data } = await axios.get('/api/search/autocomplete', {
                    params: {
                        q:                this.query,
                        commercialVehicle: this.commercial ? 'true' : 'false',
                    },
                    cancelToken: this.cancelSource.token,
                });
                this.results     = data;
                this.isOpen      = true;
                const defIdx = data.findIndex(r => r.default);
                this.activeIndex = defIdx >= 0 ? defIdx : (data.length ? 0 : -1);
            } catch (err) {
                if (!axios.isCancel(err)) {
                    this.error   = true;
                    this.results = [];
                    this.isOpen  = true;
                }
            } finally {
                this.loading = false;
            }
        },

        cancelPending() {
            if (this.cancelSource) {
                this.cancelSource.cancel();
                this.cancelSource = null;
            }
        },

        moveActive(delta) {
            if (!this.results.length) return;
            const n = this.results.length;
            this.activeIndex = ((this.activeIndex + delta) % n + n) % n;
        },

        onEnter() {
            if (!this.isOpen || !this.results.length) return;
            this.select(this.results[this.activeIndex >= 0 ? this.activeIndex : 0]);
        },

        select(row) {
            var url = new URL(this.searchUrl, window.location.origin);
            if (row.type === 'make_all') {
                url.searchParams.set('make', row.make_slug);
            } else if (row.type === 'attrs') {
                if (row.make_slug)  url.searchParams.set('make',      row.make_slug);
                if (row.model_slug) url.searchParams.set('model',     row.model_slug);
                if (row.fuel_slug)  url.searchParams.set('fuel_type', row.fuel_slug);
                if (row.body_slug)  url.searchParams.set('body_type', row.body_slug);
            } else {
                url.searchParams.set('make',  row.make_slug);
                url.searchParams.set('model', row.model_slug);
            }
            window.location.href = url.toString();
        },

        onFocus() {
            if (this.results.length) this.isOpen = true;
        },

        onBlur() {
            // Delay lets mousedown.prevent on items register before closing.
            setTimeout(() => { this.isOpen = false; }, 150);
        },

        close() {
            this.isOpen = false;
        },
    },
};
</script>
